import { IInitializable, IStaticClassName } from "./Initializable";

declare global {
    interface Window {
        Initializer: Initializer;
    }
}

export class Initializer {
    private registry: Map<string, IStaticClassName> = new Map();

    private blocks: IInitializable[] = [];
    private components: IInitializable[] = [];
    private currentPage: IInitializable | null = null;

    private static instance: Initializer;

    private constructor() {
        window.Initializer = this;
    }

    public static getInstance(): Initializer {
        return this.instance || (this.instance = new this());
    }

    static init() {
        this.getInstance().init();
    }

    public init() {
        this.initElements('[class*="-page"]', "js-", "-page", (_element, instance) => {
            this.currentPage = instance;
        });

        this.initElements('[class*="-block"]', "js-", "-block", (_element, instance) => {
            this.blocks.push(instance);
        });

        this.initElements('[class*="-component"]', "js-", "-component", (_element, instance) => {
            this.components.push(instance);
        });
    }

    private initElements(
        selector: string,
        prefix: string,
        suffix: string,
        onInit: (element: HTMLElement, instance: IInitializable, index: number) => void
    ) {
        const elements = document.querySelectorAll<HTMLElement>(selector);

        if (!elements) {
            return;
        }

        elements.forEach((element, index) => {
            const className = Array.from(element.classList).find(
                (className) => className.startsWith(prefix) && className.endsWith(suffix)
            );

            if (!className) {
                return;
            }

            const registeredClass = this.registry.get(className);

            if (!registeredClass) {
                return;
            }

            const instance = new registeredClass(element, index);
            instance.init(element, index);
            onInit(element, instance, index);
        });
    }

    /**
     * Registers a class with a static property `className`.
     * This method can be called with a single class or an array of classes.
     * @param classToRegister - The class to register. It must have a static property `className` that is a string.
     */
    public static register(classToRegister: IStaticClassName[]): void;
    public static register(classToRegister: IStaticClassName | IStaticClassName[]) {
        if (Array.isArray(classToRegister)) {
            classToRegister.forEach((cls) => this.getInstance().register(cls));
            return;
        }
        this.getInstance().register(classToRegister);
    }

    public register(classToRegister: IStaticClassName): void {
        this.registry.set(classToRegister.className, classToRegister);
    }
}
